import { createLogger } from "../../core/logger";
import type { LogEntry } from "../../core/eventLog";
import { JsonTree } from "../JsonTree";
import { safeStringify } from "../format";

const log = createLogger("ui.inspector");

type Props = {
  entry: LogEntry | undefined;
};

export function InspectorPanel({ entry }: Props): React.JSX.Element {
  if (!entry) {
    return (
      <p className="empty-state">
        Select a timeline row to inspect its payload.
      </p>
    );
  }

  const copyRaw = (): void => {
    const text = safeStringify(entry, 2);
    navigator.clipboard?.writeText(text).catch((e: unknown) => {
      log.warn("clipboard write failed", e);
    });
  };

  return (
    <div className="inspector-panel">
      <div className="inspector-meta">
        <span>
          #{entry.seq} · {entry.ts}
        </span>
        <span className={`badge badge-${entry.kind}`}>{entry.kind}</span>
        <span>
          {entry.moduleId}.{entry.name}
        </span>
        {entry.contactId ? <span>contact: {entry.contactId}</span> : null}
        {entry.durationMs !== undefined ? (
          <span>{entry.durationMs}ms</span>
        ) : null}
        <button type="button" onClick={copyRaw}>
          Copy raw JSON
        </button>
      </div>

      {entry.kind === "error" && entry.error ? (
        <div className="inspector-error" role="alert">
          <p className="inspector-error-name">{entry.error.name ?? "Error"}</p>
          <p className="inspector-error-message">{entry.error.message}</p>
          {entry.error.stack ? (
            <pre className="inspector-error-stack">{entry.error.stack}</pre>
          ) : null}
        </div>
      ) : null}

      <div className="inspector-payload">
        {entry.payload === undefined ? (
          <p className="empty-state">No payload.</p>
        ) : (
          <JsonTree value={entry.payload} />
        )}
      </div>
    </div>
  );
}
